import React, { FC, useCallback, useMemo, useState } from 'react';
import ModalLayout from '../../../../shared/components/modal/ModalLayout';
import { StHeader, StModalButtonLine } from '../../../../shared/components/modal/ConfirmationModalLayout';
import { CAT_MAN_PRODUCT_GROUP_SORTING } from '../../../../shared/common/SortByDropDown';
import { DropDownMenu } from '../../../../shared/common/DropDownMenu';
import MaterialIcon from '../../../../shared/common/MaterialIcon';
import styled from 'styled-components';
import { Button, ButtonSize, ButtonTheme } from '../../../../shared/common/Button';
import { StCancelButton } from '../../../../shop/reserve/components/ReserveListDetailPopup';
import { HelpTooltip } from '../../../../shared/common/HelpTooltip';
import { useTranslation } from 'react-i18next';

interface Props {
    title: string;
    visible: boolean;
    handleCancel: () => void;
    sorting: string;
    onSortChange: (item: SortingItem) => void;
    onSave: () => void;
}

export type SortingItem = {
    value: string;
    label: string;
};

const ListItem = styled.li`
    position: relative;
    min-width: 200px;
    cursor: pointer;
    transition-duration: 0.2s;

    &:hover {
        color: #7fc31c;
    }

    padding: 2px 10px;

    .material-icon {
        top: 3px;
        right: 5px;
        transform: none;
    }
`;

const SortingLabel = styled.div`
    font-size: 15px !important;
    margin-left: 5px;
`;

const SortingWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-start;
`;

const EditPropertiesModal: FC<Props> = ({ title, visible, handleCancel, sorting, onSortChange, onSave }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const header = useMemo(() => <StHeader>{title}</StHeader>, [title]);

    const confirmationButton = useMemo(
        () => (
            <Button
                text={t('confirm')}
                onClick={onSave}
                data-displayname="ConfirmationButton"
                size={ButtonSize.LARGE}
            />
        ),
        [onSave, t]
    );

    const sortingLabel = useMemo(() => {
        return (
            <SortingLabel>
                <span>{t('marketing.productGroups.sortBy')}&nbsp;</span>
                <b>{t(`components.catManProductGroupSorting.${sorting}`)}</b>
            </SortingLabel>
        );
    }, [sorting, t]);

    const handleSortChange = useCallback(
        item => {
            onSortChange(item);
            setExpanded(false);
        },
        [onSortChange]
    );

    const cancelButton = useMemo(() => {
        return (
            <StCancelButton
                size={ButtonSize.LARGE}
                theme={ButtonTheme.POSITIVE_INVERT}
                text={t('cancel')}
                onClick={handleCancel}
            />
        );
    }, [handleCancel, t]);

    const content = useMemo(() => {
        return (
            <SortingWrapper>
                <HelpTooltip size="large" className="ml-sm">
                    <p>{t('marketing.productGroups.sortTooltip')}</p>
                </HelpTooltip>
                <DropDownMenu
                    label={sortingLabel}
                    expanded={expanded}
                    align="left"
                    onToggleClick={() => setExpanded(!expanded)}
                    onClickOutside={() => setExpanded(false)}
                    className="age-category-selector"
                >
                    {CAT_MAN_PRODUCT_GROUP_SORTING.map(item => {
                        const isActive = item.label === sorting;
                        return (
                            <ListItem
                                className="age-category-selector-item"
                                key={item.value}
                                onClick={() => handleSortChange(item)}
                            >
                                <span>{t(`components.catManProductGroupSorting.${item.value}`)}</span>
                                {isActive ? <MaterialIcon icon="check" primary={true} /> : null}
                            </ListItem>
                        );
                    })}
                </DropDownMenu>
            </SortingWrapper>
        );
    }, [expanded, handleSortChange, sorting, sortingLabel, t]);
    const footer = useMemo(() => {
        return (
            <StModalButtonLine>
                {cancelButton}
                {confirmationButton}
            </StModalButtonLine>
        );
    }, [cancelButton, confirmationButton]);
    return (
        <ModalLayout
            header={header}
            content={content}
            footer={footer}
            visible={visible}
            width={530}
            closable={false}
            closeOnEsc={false}
        />
    );
};

export default EditPropertiesModal;
